import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Grid,
  Heading,
  HStack,
  IconButton,
  Input,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { Pause, Play, Search } from "lucide-react";

function statusColor(status) {
  switch (status) {
    case "completed":
      return "green";
    case "running":
      return "blue";
    case "failed":
      return "red";
    case "budget_exhausted":
      return "orange";
    default:
      return "gray";
  }
}

function formatMoney(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function titleize(value) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

export default function GooglePlaces() {
  const [campaignPayload, setCampaignPayload] = useState(null);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [campaignActionId, setCampaignActionId] = useState(null);
  const [campaignForm, setCampaignForm] = useState({
    name: "",
    industries: ["plumbers", "hvac", "landscapers"],
    regions: ["OR", "WA"],
  });
  const toast = useToast();

  const loadCampaigns = async () => {
    try {
      setCampaignsLoading(true);
      const response = await fetch("/api/lead_campaigns", {
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Failed to load Google Places campaigns");

      setCampaignPayload(data);
    } catch (err) {
      toast({
        title: "Failed to load Google Places campaigns",
        description: err.message || "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCampaignsLoading(false);
    }
  };

  useEffect(() => {
    loadCampaigns();
  }, []);

  const startCampaign = async () => {
    try {
      setImporting(true);
      const response = await fetch("/api/lead_campaigns", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify(campaignForm),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Failed to start Google Places campaign");

      toast({
        title: "Google Places campaign started",
        description: "Google Places search is running in the background.",
        status: "success",
        duration: 3500,
        isClosable: true,
      });
      setCampaignForm((current) => ({ ...current, name: "" }));
      loadCampaigns();
    } catch (err) {
      toast({
        title: "Failed to start Google Places campaign",
        description: err.message || "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setImporting(false);
    }
  };

  const updateCampaign = async (campaignId, action) => {
    try {
      setCampaignActionId(campaignId);
      const response = await fetch(`/api/lead_campaigns/${campaignId}/${action}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || `Failed to ${action} campaign`);

      toast({
        title: action === "pause" ? "Campaign paused" : "Campaign resumed",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      loadCampaigns();
    } catch (err) {
      toast({
        title: `Failed to ${action} campaign`,
        description: err.message || "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCampaignActionId(null);
    }
  };

  if (campaignsLoading && !campaignPayload) {
    return (
      <Box maxW="1500px" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
        <VStack spacing={3}>
          <Spinner color="blue.500" />
          <Text color="gray.600">Loading Google Places campaigns...</Text>
        </VStack>
      </Box>
    );
  }

  const campaigns = campaignPayload?.campaigns || [];
  const options = campaignPayload?.options || {};
  const industries = options.industries || [];
  const regions = options.regions || [];
  const selectedTargetCount = (campaignForm.industries.length || 0) * (campaignForm.regions.length || 0);

  return (
    <Box maxW="1500px" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
      <VStack align="stretch" spacing={5}>
        <HStack justify="space-between" align="start" spacing={4}>
          <Box>
            <Heading size="lg">Google Places</Heading>
            <Text mt={1} color="gray.600" fontSize="sm">
              Multi-market Google Places lead discovery with Jobber and ServiceM8 website detection.
            </Text>
          </Box>
          <HStack spacing={3}>
            <Button as={RouterLink} to="/leads" variant="outline" leftIcon={<Search size={16} />}>
              View Leads
            </Button>
            <Button
              colorScheme="blue"
              leftIcon={importing ? <Spinner size="xs" /> : <Search size={16} />}
              onClick={startCampaign}
              isDisabled={importing || campaignForm.industries.length === 0 || campaignForm.regions.length === 0}
            >
              {importing ? "Starting..." : "Start Campaign"}
            </Button>
          </HStack>
        </HStack>

        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
          <VStack align="stretch" spacing={4}>
            <Box>
              <Heading size="sm">New Google Places Campaign</Heading>
              <Text mt={1} color="gray.600" fontSize="sm">
                {selectedTargetCount} state/industry groups selected. Google spend is tracked as the campaign runs.
              </Text>
            </Box>
            <Input
              value={campaignForm.name}
              onChange={(event) => setCampaignForm((current) => ({ ...current, name: event.target.value }))}
              bg="white"
              placeholder="Campaign name"
            />
            <Box>
              <Text mb={2} fontSize="sm" fontWeight="semibold">Industries</Text>
              <CheckboxGroup
                value={campaignForm.industries}
                onChange={(values) => setCampaignForm((current) => ({ ...current, industries: values }))}
              >
                <SimpleGrid columns={{ base: 2, md: 5 }} spacing={2}>
                  {industries.map((industry) => (
                    <Checkbox key={industry} value={industry}>{titleize(industry)}</Checkbox>
                  ))}
                </SimpleGrid>
              </CheckboxGroup>
            </Box>
            <Box>
              <Text mb={2} fontSize="sm" fontWeight="semibold">States / Regions</Text>
              <CheckboxGroup
                value={campaignForm.regions}
                onChange={(values) => setCampaignForm((current) => ({ ...current, regions: values }))}
              >
                <SimpleGrid columns={{ base: 3, md: 8, xl: 13 }} spacing={2}>
                  {regions.map((region) => (
                    <Checkbox key={region} value={region}>{region}</Checkbox>
                  ))}
                </SimpleGrid>
              </CheckboxGroup>
            </Box>
          </VStack>
        </Box>

        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" overflowX="auto">
          <Table size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Campaign</Th>
                <Th>Status</Th>
                <Th>Spend</Th>
                <Th>Targets</Th>
                <Th>Leads</Th>
                <Th>Software</Th>
                <Th textAlign="right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {campaigns.map((campaign) => (
                <Tr key={campaign.id}>
                  <Td minW="260px">
                    <Text fontWeight="semibold">{campaign.name}</Text>
                    <Text fontSize="xs" color="gray.600">
                      {(campaign.industries || []).map(titleize).join(", ")} in {(campaign.regions || []).join(", ")}
                    </Text>
                    {campaign.last_error && <Text fontSize="xs" color="red.600" noOfLines={1}>{campaign.last_error}</Text>}
                  </Td>
                  <Td>
                    <Badge colorScheme={statusColor(campaign.status)}>{campaign.status}</Badge>
                  </Td>
                  <Td whiteSpace="nowrap">
                    <Text>{formatMoney(campaign.estimated_google_spend_dollars)}</Text>
                    <Text fontSize="xs" color="gray.600">{campaign.google_request_count} requests</Text>
                  </Td>
                  <Td whiteSpace="nowrap">
                    <Text>{campaign.completed_target_count} / {campaign.target_count}</Text>
                    <Text fontSize="xs" color="gray.600">{campaign.failed_target_count} failed</Text>
                  </Td>
                  <Td whiteSpace="nowrap">
                    <Text>{campaign.unique_leads_count} unique</Text>
                    <Text fontSize="xs" color="gray.600">{campaign.website_leads_count} web · {campaign.phone_leads_count} phone</Text>
                  </Td>
                  <Td whiteSpace="nowrap">
                    <Text>{campaign.jobber_positive_count} Jobber</Text>
                    <Text fontSize="xs" color="purple.700">{campaign.service_m8_positive_count} ServiceM8</Text>
                  </Td>
                  <Td textAlign="right">
                    <HStack justify="flex-end">
                      <IconButton
                        aria-label="Filter leads by campaign"
                        icon={<Search size={16} />}
                        size="sm"
                        variant="ghost"
                        as={RouterLink}
                        to={`/leads?campaign_id=${campaign.id}`}
                      />
                      {campaign.status === "running" ? (
                        <IconButton
                          aria-label="Pause campaign"
                          icon={campaignActionId === campaign.id ? <Spinner size="xs" /> : <Pause size={16} />}
                          size="sm"
                          variant="ghost"
                          isDisabled={campaignActionId === campaign.id}
                          onClick={() => updateCampaign(campaign.id, "pause")}
                        />
                      ) : (
                        <IconButton
                          aria-label="Resume campaign"
                          icon={campaignActionId === campaign.id ? <Spinner size="xs" /> : <Play size={16} />}
                          size="sm"
                          variant="ghost"
                          isDisabled={campaignActionId === campaign.id || campaign.status === "completed"}
                          onClick={() => updateCampaign(campaign.id, "resume")}
                        />
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
              {campaigns.length === 0 && (
                <Tr>
                  <Td colSpan={7}>
                    <Text color="gray.600" textAlign="center" py={6}>No Google Places campaigns yet.</Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      </VStack>
    </Box>
  );
}
